import { Head, usePage } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { Download, FileText } from 'lucide-react'
import AppLayout from '@components/layout/Layout/AppLayout'
import Breadcrumb, { BreadcrumbItem } from '@components/layout/Breadcrumb'
import PDFViewer from '@features/document-preview/components/PDFViewer/PDFViewer'
import ImageViewer from '@features/document-preview/components/ImageViewer/ImageViewer'
import EmailViewer from '@features/document-preview/components/EmailViewer/EmailViewer'
import UserAvatar from '@components/UserAvatar'
import DateDisplay from '@features/file-explorer/components/Table/DateDisplay'
import MimeTypeIcon from '@features/file-explorer/components/Table/MimeTypeBadge'
import { User } from '@shared-types/common'
import { SharedUser } from '@features/file-explorer/types'
import { formatBytes } from '@utils/formatBytes'

interface DocumentProps {
  id: string
  name: string
  description: string | null
  size: number
  mime_type: string
  file_url: string
  preview_url: string | null
  extension: string
  created_at: string
  updated_at: string
  owner: User | null
  shared_with: SharedUser[]
  can_manage_permissions: boolean
}

interface PreviewProps {
  document: DocumentProps
  current_folder_id: string | null
  breadcrumbs: BreadcrumbItem[]
}

export default function Preview({ document, current_folder_id: _current_folder_id, breadcrumbs }: PreviewProps) {
  const { t } = useTranslation()
  const { user } = usePage<{ user: any }>().props

  // File type detection
  const isPDF = document.mime_type === 'application/pdf' || document.extension.toLowerCase() === 'pdf'

  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico']
  const isImage = document.mime_type?.startsWith('image/') ||
    imageExtensions.includes(document.extension.toLowerCase())

  const emailExtensions = ['eml', 'msg']
  const isEmail = document.mime_type === 'message/rfc822' ||
    emailExtensions.includes(document.extension.toLowerCase())

  return (
    <AppLayout user={user}>
      <Head title={`${t('documents.preview.title')} - ${document.name}`} />

      <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4">
        {/* Breadcrumb Navigation */}
        <div className="mb-2 sm:mb-4">
          <Breadcrumb items={breadcrumbs} currentItemId={document.id} />
        </div>


        {/* Preview */}
        <div className="card bg-base-100 shadow-xl h-[calc(100vh-10rem)] sm:h-[calc(100vh-10rem)] flex flex-col">
          {/* Document info header - compact bar (sticky) */}
          <div className="flex-shrink-0 border-b border-base-300 bg-base-100 z-10">
            <div className="px-4 py-2 flex items-center justify-between gap-4">
              {/* Left side: File info */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {/* File type icon and name */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <MimeTypeIcon mimeType={document.mime_type} extension={document.extension} />
                  <div className="min-w-0 flex-1">
                    <h1 className="text-base font-semibold truncate">{document.name}</h1>
                    {document.description && (
                      <p className="text-xs text-base-content/60 truncate">
                        {document.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Metadata - hidden on mobile */}
                <div className="hidden lg:flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    {document.owner && <UserAvatar user={document.owner} size="sm" />}
                  </div>
                  <div className="text-base-content/70">
                    {formatBytes(document.size)}
                  </div>
                  <div>
                    <DateDisplay createdAt={document.created_at} updatedAt={document.updated_at} />
                  </div>
                </div>
              </div>

              {/* Right side: Actions */}
              <a
                href={document.file_url}
                download
                className="btn btn-primary btn-sm gap-2"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t('documents.download')}</span>
              </a>
            </div>
          </div>

          {/* Preview content area (scrollable) */}
          <div className="flex-1 overflow-hidden">
            {isPDF ? (
              <PDFViewer fileUrl={document.file_url} fileName={document.name} />
            ) : isImage ? (
              <ImageViewer imageUrl={document.file_url} fileName={document.name} />
            ) : isEmail ? (
              <EmailViewer fileUrl={document.file_url} fileName={document.name} />
            ) : (
              <div className="card-body items-center text-center py-16">
                <FileText className="w-24 h-24 opacity-20 mb-4" />
                <h2 className="card-title text-xl mb-2">
                  {t('documents.preview.placeholder.title')}
                </h2>
                <p className="text-base-content/70 max-w-md">
                  {t('documents.preview.placeholder.description')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
